import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup } from '@angular/forms';
import { FormsUtils } from '../../shared/forms.utils';
import { DependentFieldFilters, Field } from '../../shared/interfaces/hubspot-form-data';
import { HubspotOperators } from '../../shared/services/hubspot/hubspot-operators';
import { FormUtilsService } from '../../shared/services/form-utils.service';
import { ValidatorUtils } from '../../shared/services/validator-utils';
import { FormsService } from '../../shared/services/forms.service';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ConfigurationService } from '@draeger-academy/common';

@Component({
    selector: 'da-form-field-phone',
    templateUrl: './phone.component.html',
    styleUrls: ['./phone.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhoneComponent implements OnInit, OnDestroy {
    field: Field;
    group: UntypedFormGroup;
    public countriesByCode: any;
    public phonePrefixByCode: any;
    public formId: string;
    public hasFocus = false;
    public portalCountry = 'DE';
    private _$showCountrySelect: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$isSubmitting: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$phonePrefix: BehaviorSubject<string> = new BehaviorSubject('');
    private _$numberWithoutPrefix: BehaviorSubject<string> = new BehaviorSubject('');
    public preselectedCountry: string;
    private _inititalDependentFields: string;
    private _serviceSubscription: Subscription;

    constructor(
        private _hubspotOperators: HubspotOperators,
        private _formUtils: FormUtilsService,
        private _FormsService: FormsService,
        protected _ref: ChangeDetectorRef,
        private config: ConfigurationService
    ) {}

    public get showErrors$(): Observable<boolean> {
        const _control = this.group.get(this.field.name);
        const _showErrors = _control.touched || this._$isSubmitting.getValue();
        return of(_showErrors);
    }

    public get showCountrySelect$(): Observable<boolean> {
        return this._$showCountrySelect.asObservable();
    }

    public get phonePrefix$(): Observable<string> {
        return this._$phonePrefix.asObservable();
    }

    public ngOnInit() {
        this._inititalDependentFields = JSON.stringify(this.field.dependentFieldFilters);
        this._getDropdownValuesFromConfig();
        if (!this.group.get(this.field.name).value) {
            this._getPreselectionForDropdown();
        }
        const _hasCCMetadata = this.field.metaData.filter((data) => data.name === 'useCountryCodeSelect')[0];
        this._$showCountrySelect.next(_hasCCMetadata && _hasCCMetadata.value ? _hasCCMetadata.value : false);
        this._serviceSubscription = this._FormsService
            .submitting$(this.formId)
            .pipe(distinctUntilChanged())
            .subscribe((isSubmitting) => {
                if (isSubmitting) {
                    this.group.get(this.field.name).markAsTouched();
                    this._markDependentFieldsAsTouched();
                    this._ref.detectChanges();
                }
                this._$isSubmitting.next(isSubmitting);
            });
    }
    ngOnDestroy() {
        this._serviceSubscription.unsubscribe();
    }

    public getErrorTranslation(error: any): string {
        const errorTranslation = this._formUtils.getErrorTranslation(error, this.group, this.field);
        return errorTranslation ? this._formUtils.replaceMinMax(this.field, errorTranslation) : '';
    }

    public getVisibilityByDependentFieldFiltersAndSetValidators(
        dependentField: DependentFieldFilters,
        parentField: AbstractControl
    ): boolean {
        const _isVisible = this._hubspotOperators.shouldBeVisible(dependentField, parentField, this.field);
        const _currentDependentField = this.group.get(dependentField.dependentFormField.name);
        const _currentDependentFieldIsRequiredWhenVisible = this._formUtils.isFieldInitiallyRequired(
            dependentField,
            this._inititalDependentFields
        );
        _currentDependentField.setValidators(
            ValidatorUtils.setValidatorsAgain(dependentField, _isVisible, _currentDependentFieldIsRequiredWhenVisible)
        );
        _currentDependentField.updateValueAndValidity();
        return _isVisible;
    }
    public updatePhonePrefix(e: string) {
        const _phonePrefix = this.phonePrefixByCode[e];
        this._$phonePrefix.next(_phonePrefix);
        this._updatePhoneComplete();
    }

    public updatePhoneNumberValue(e: any) {
        this._$numberWithoutPrefix.next(e.target.value);
        this._updatePhoneComplete();
    }

    public validateField() {
        this.group.get(this.field.name).markAsTouched();
        this.group.get(this.field.name).updateValueAndValidity();
        this._ref.detectChanges();
    }

    private _getDropdownValuesFromConfig() {
        this.countriesByCode = this.config.$allCountriesByLanguage.getValue();
        this.phonePrefixByCode = FormsUtils.getPhonePrefixByCode();
    }

    private _updatePhoneComplete() {
        const _phoneComplete = this._$phonePrefix.getValue() + ' ' + this._$numberWithoutPrefix.getValue();
        this.group.get(this.field.name).setValue(_phoneComplete);
    }

    private _getPreselectionForDropdown() {
        if (this.countriesByCode && this.phonePrefixByCode) {
            const _isPortal = this.config.isPartnerPage();

            if (_isPortal) {
                this.portalCountry = JSON.parse(localStorage.getItem('portalCountryCode')).countryCode.split('_')[1].toLocaleUpperCase();
                this.preselectedCountry = this.portalCountry;
                this.updatePhonePrefix(this.portalCountry);
            } else {
                const _site = this.config.getCurrentSite();
                const _country = _site.split('_')[1].toLocaleUpperCase();
                this.preselectedCountry = _country;
                this.updatePhonePrefix(_country);
            }
        }
    }

    private _replaceMinMax(errorTranslation: string) {
        const validationData = this.field.validation.data.split(':');
        if (validationData[0]) {
            errorTranslation = errorTranslation.replace('{{ min }}', validationData[0]);
        }
        if (validationData[1]) {
            errorTranslation = errorTranslation.replace('{{ max }}', validationData[1]);
        }
        return errorTranslation;
    }

    private _markDependentFieldsAsTouched() {
        this.field.dependentFieldFilters.forEach((filter) => this.group.get(filter.dependentFormField.name).markAsTouched());
    }
}
